import React, { forwardRef } from 'react';
import { achievementCircleColor } from '../utils/achievementRarity';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import {
  ACHIEVEMENT_SHARE_CARD_HEIGHT,
  ACHIEVEMENT_SHARE_CARD_WIDTH,
} from '../utils/shareAchievementImage';
import './AchievementShareTemplate.css';

export interface AchievementShareTemplateProps {
  /** Localized line, e.g. "Player: nickname" / "Jogador: nick". */
  playerLineText: string;
  /** Base name for `/assets/achievements/{key}.png`; trophy fallback if empty. */
  iconAssetKey?: string | null;
  rarity: string;
  /** Localized line, e.g. "Rarity: Common" / "Raridade: Comum". */
  rarityLineText: string;
  siteUrl: string;
  headlineText: string;
  /** Achievement description (localized); omitted when empty. */
  descriptionText?: string;
  /** Localized line with unlock date, e.g. "Unlocked on …" / "Obtida em …"; omitted when empty. */
  unlockedDateLineText?: string;
}

const FONT_ORBITRON = "'Orbitron', system-ui, sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";

const glassStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 20,
};

/** Deterministic confetti for static “party” look (html-to-image friendly). */
const SHARE_CONFETTI: ReadonlyArray<{
  left: string;
  top: string;
  w: number;
  h: number;
  bg: string;
  rot: number;
}> = [
  { left: '6%', top: '10%', w: 6, h: 5, bg: '#FFFF00', rot: 45 },
  { left: '14%', top: '6%', w: 5, h: 7, bg: '#00FFFF', rot: 45 },
  { left: '22%', top: '18%', w: 7, h: 6, bg: '#FF00FF', rot: 45 },
  { left: '88%', top: '8%', w: 6, h: 6, bg: '#FFFF00', rot: 45 },
  { left: '78%', top: '14%', w: 5, h: 5, bg: '#00FFFF', rot: 45 },
  { left: '92%', top: '22%', w: 8, h: 5, bg: '#FF00FF', rot: 45 },
  { left: '4%', top: '28%', w: 5, h: 8, bg: '#FF6600', rot: 45 },
  { left: '11%', top: '42%', w: 7, h: 7, bg: '#00FF88', rot: 45 },
  { left: '8%', top: '58%', w: 6, h: 4, bg: '#FFFF00', rot: 45 },
  { left: '16%', top: '72%', w: 5, h: 6, bg: '#00FFFF', rot: 45 },
  { left: '10%', top: '88%', w: 8, h: 6, bg: '#FF00FF', rot: 45 },
  { left: '90%', top: '38%', w: 6, h: 7, bg: '#FFFF00', rot: 45 },
  { left: '84%', top: '52%', w: 5, h: 5, bg: '#00FF88', rot: 45 },
  { left: '93%', top: '68%', w: 7, h: 5, bg: '#00FFFF', rot: 45 },
  { left: '86%', top: '82%', w: 6, h: 6, bg: '#FF6600', rot: 45 },
  { left: '34%', top: '5%', w: 5, h: 6, bg: '#FF00FF', rot: 45 },
  { left: '52%', top: '7%', w: 6, h: 5, bg: '#FFFF00', rot: 45 },
  { left: '66%', top: '6%', w: 7, h: 7, bg: '#00FFFF', rot: 45 },
  { left: '42%', top: '92%', w: 6, h: 6, bg: '#00FFFF', rot: 45 },
  { left: '58%', top: '90%', w: 5, h: 7, bg: '#FFFF00', rot: 45 },
  { left: '72%', top: '93%', w: 8, h: 5, bg: '#FF00FF', rot: 45 },
  { left: '48%', top: '24%', w: 5, h: 5, bg: '#FF6600', rot: 45 },
  { left: '30%', top: '48%', w: 6, h: 8, bg: '#00FF88', rot: 45 },
  { left: '70%', top: '44%', w: 7, h: 6, bg: '#FFFF00', rot: 45 },
  { left: '38%', top: '78%', w: 5, h: 5, bg: '#00FFFF', rot: 45 },
  { left: '62%', top: '76%', w: 6, h: 6, bg: '#FF00FF', rot: 45 },
  { left: '26%', top: '62%', w: 4, h: 6, bg: '#FFFF00', rot: 45 },
  { left: '76%', top: '60%', w: 6, h: 4, bg: '#00FFFF', rot: 45 },
];

function medalPresentation(rarity: string): {
  outerGlow: string;
  innerRing: string;
  radialBehind: string;
} {
  const r = rarity?.toLowerCase() || '';
  if (r.includes('epic')) {
    return {
      outerGlow: '0 0 50px rgba(255, 215, 0, 0.35), 0 12px 40px rgba(0,0,0,0.45)',
      innerRing: 'inset 0 0 0 4px rgba(255, 215, 0, 0.45)',
      radialBehind:
        'radial-gradient(circle, rgba(255, 215, 0, 0.22) 0%, rgba(255, 215, 0, 0.08) 40%, transparent 70%)',
    };
  }
  if (r.includes('rare')) {
    return {
      outerGlow: '0 0 42px rgba(0, 163, 255, 0.38), 0 12px 40px rgba(0,0,0,0.45)',
      innerRing: 'inset 0 0 0 4px rgba(0, 163, 255, 0.42)',
      radialBehind:
        'radial-gradient(circle, rgba(0, 163, 255, 0.2) 0%, rgba(0, 163, 255, 0.06) 42%, transparent 72%)',
    };
  }
  return {
    outerGlow: '0 0 36px rgba(255, 255, 255, 0.14), 0 12px 36px rgba(0,0,0,0.4)',
    innerRing: 'inset 0 0 0 3px rgba(255, 255, 255, 0.18)',
    radialBehind:
      'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.06) 45%, transparent 70%)',
  };
}

/**
 * Card captured by html-to-image. Critical layout/typography are inline so exports stay consistent.
 */
const AchievementShareTemplate = forwardRef<HTMLDivElement, AchievementShareTemplateProps>(
  function AchievementShareTemplate(
    {
      playerLineText,
      iconAssetKey,
      rarity,
      rarityLineText,
      siteUrl,
      headlineText,
      descriptionText,
      unlockedDateLineText,
    },
    ref
  ) {
    const bg = achievementCircleColor(rarity);
    const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
    const medal = medalPresentation(rarity);
    const achievementIconSrc = achievementIconSrcFromKey(iconAssetKey);

    return (
      <div
        ref={ref}
        className="achievement-share-template-root"
        data-testid="achievement-share-template"
        style={{
          width: ACHIEVEMENT_SHARE_CARD_WIDTH,
          height: ACHIEVEMENT_SHARE_CARD_HEIGHT,
          boxSizing: 'border-box',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
          fontFamily: FONT_INTER,
          background: 'linear-gradient(180deg, #2D1B4E 0%, #160D28 100%)',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            padding: '36px 44px 32px',
            boxSizing: 'border-box',
            color: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Confetes estáticos */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 0,
              overflow: 'hidden',
            }}
          >
            {SHARE_CONFETTI.map((c, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: c.left,
                  top: c.top,
                  width: c.w,
                  height: c.h,
                  backgroundColor: c.bg,
                  borderRadius: 2,
                  transform: `rotate(${c.rot}deg)`,
                }}
              />
            ))}
          </div>

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Cabeçalho: vidro + logo + Quizter Club */}
            <div
              style={{
                flexShrink: 0,
                ...glassStyle,
                padding: '20px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <img
                src={logoSrc}
                alt=""
                style={{
                  height: 56,
                  width: 'auto',
                  maxWidth: 200,
                  objectFit: 'contain',
                  display: 'block',
                }}
                decoding="async"
              />
              <div
                style={{
                  fontFamily: FONT_ORBITRON,
                  fontWeight: 700,
                  fontSize: 40,
                  lineHeight: 1.1,
                  color: '#ffffff',
                  textAlign: 'center',
                  textShadow: '2px 2px 0px #000000',
                  letterSpacing: '0.06em',
                }}
              >
                Quizter Club
              </div>
            </div>

            {/* Medalhão + jogador */}
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
                marginTop: 20,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: 200,
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    width: '112%',
                    height: '112%',
                    left: '-6%',
                    top: '-6%',
                    borderRadius: '50%',
                    background: medal.radialBehind,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    backgroundColor: bg,
                    boxShadow: `${medal.innerRing}, ${medal.outerGlow}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {achievementIconSrc ? (
                    <img
                      src={achievementIconSrc}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: 88,
                        lineHeight: 1,
                      }}
                      aria-hidden
                    >
                      🏆
                    </span>
                  )}
                </div>
              </div>
              <div
                style={{
                  fontFamily: FONT_INTER,
                  fontSize: 26,
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.9)',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.3,
                  maxWidth: '100%',
                }}
              >
                {playerLineText}
              </div>
            </div>

            {/* Destaque + descrição (vidro) */}
            <div
              style={{
                flex: '1 1 0',
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 18,
                marginBottom: 10,
                paddingLeft: 4,
                paddingRight: 4,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  ...glassStyle,
                  width: '100%',
                  padding: '22px 20px',
                  boxSizing: 'border-box',
                }}
              >
                <p
                  style={{
                    fontFamily: FONT_INTER,
                    fontSize: 38,
                    fontWeight: 800,
                    lineHeight: 1.28,
                    color: '#ffffff',
                    margin: 0,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {headlineText}
                </p>
                {descriptionText?.trim() ? (
                  <p
                    style={{
                      fontFamily: FONT_INTER,
                      fontSize: 30,
                      fontWeight: 500,
                      lineHeight: 1.45,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: '16px 0 0',
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {descriptionText.trim()}
                  </p>
                ) : null}
              </div>
            </div>

            {/* Rodapé */}
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                paddingBottom: 4,
              }}
            >
              <div
                style={{
                  fontFamily: FONT_INTER,
                  fontSize: 24,
                  fontWeight: 600,
                  color: '#FFD700',
                  textAlign: 'center',
                }}
              >
                {rarityLineText}
              </div>
              {unlockedDateLineText?.trim() ? (
                <div
                  style={{
                    fontFamily: FONT_INTER,
                    fontSize: 22,
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.85)',
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  {unlockedDateLineText.trim()}
                </div>
              ) : null}
              <div
                style={{
                  fontFamily: FONT_INTER,
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#00A3FF',
                  textAlign: 'center',
                  letterSpacing: '0.02em',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%',
                  paddingLeft: 32,
                  paddingRight: 32,
                  boxSizing: 'border-box',
                }}
              >
                {siteUrl}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default AchievementShareTemplate;
